/**
 * FormFieldsEditorModal — Google-Forms-style builder for the Form Collection
 * pipeline stage's candidate information form. Recruiter has full freedom to
 * add/remove/reorder fields, pick a type, mark required or optional, and set
 * per-type validation (min/max length, regex pattern, numeric/date range,
 * how many checkboxes must be selected). Mirrors form_validation.py's schema
 * exactly — the backend re-validates and sanitizes on save regardless of
 * what this UI already checked, so this only needs to give good UX, not be
 * the last line of defense.
 */
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getFormFields, saveFormFields, FORM_FIELD_TYPES, FORM_CHOICE_TYPES } from "../services/jobService";
import "./RecruiterJobs.css";

let _nextLocalId = 1;
const withLocalId = (field) => ({ ...field, _localId: field._localId || `f${_nextLocalId++}` });

const blankField = () => withLocalId({
  key: "", label: "", type: "text", required: false, helpText: "", placeholder: "",
  options: ["", ""], validation: {},
});

const VALIDATION_FIELDS_BY_TYPE = {
  text: [
    { key: "minLength", label: "Min length", type: "number" },
    { key: "maxLength", label: "Max length", type: "number" },
    { key: "pattern", label: "Pattern (regex, optional)", type: "text", wide: true },
  ],
  textarea: [
    { key: "minLength", label: "Min length", type: "number" },
    { key: "maxLength", label: "Max length", type: "number" },
    { key: "pattern", label: "Pattern (regex, optional)", type: "text", wide: true },
  ],
  number: [
    { key: "min", label: "Min value", type: "number" },
    { key: "max", label: "Max value", type: "number" },
  ],
  date: [
    { key: "minDate", label: "Earliest date", type: "date" },
    { key: "maxDate", label: "Latest date", type: "date" },
  ],
  checkbox: [
    { key: "minSelected", label: "Min selections", type: "number" },
    { key: "maxSelected", label: "Max selections", type: "number" },
  ],
};

function FieldCard({ field, index, total, onUpdate, onUpdateValidation, onRemove, onDragStart, onDragOver, onDrop }) {
  const isChoice = FORM_CHOICE_TYPES.has(field.type);
  const validationFields = VALIDATION_FIELDS_BY_TYPE[field.type] || [];

  const setOptions = (next) => onUpdate({ options: next });
  const addOption = () => setOptions([...(field.options || []), ""]);
  const updateOption = (i, value) => setOptions((field.options || []).map((o, idx) => (idx === i ? value : o)));
  const removeOption = (i) => setOptions((field.options || []).filter((_, idx) => idx !== i));

  return (
    <div className="form-field-card" onDragOver={onDragOver} onDrop={onDrop}>
      <div className="form-field-card-header">
        <span className="form-field-drag-handle" draggable onDragStart={onDragStart} title="Drag to reorder">
          ⠿ Field {index + 1} of {total}
        </span>
        <button type="button" className="icon-button" onClick={onRemove} aria-label="Remove field">x</button>
      </div>

      <div className="form-field-row">
        <label className="form-field-label-input">
          <span>Label</span>
          <input type="text" value={field.label} placeholder="Question label"
            onChange={(e) => onUpdate({ label: e.target.value })} />
        </label>
        <label className="form-field-type-select">
          <span>Type</span>
          <select value={field.type} onChange={(e) => onUpdate({ type: e.target.value, validation: {} })}>
            {FORM_FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        <label className="form-field-required-toggle">
          <input type="checkbox" checked={!!field.required} onChange={(e) => onUpdate({ required: e.target.checked })} />
          <span>Required</span>
        </label>
      </div>

      <div className="form-field-row">
        <label className="form-field-label-input">
          <span>Help text (optional)</span>
          <input type="text" value={field.helpText || ""} placeholder="Shown under the question"
            onChange={(e) => onUpdate({ helpText: e.target.value })} />
        </label>
        {!isChoice && (
          <label className="form-field-label-input">
            <span>Placeholder (optional)</span>
            <input type="text" value={field.placeholder || ""} placeholder="Example answer"
              onChange={(e) => onUpdate({ placeholder: e.target.value })} />
          </label>
        )}
      </div>

      {isChoice && (
        <div className="form-field-options">
          <span className="job-form-block-label">Options</span>
          {(field.options || []).map((opt, i) => (
            <div className="form-field-option-row" key={i}>
              <input type="text" value={opt} placeholder={`Option ${i + 1}`}
                onChange={(e) => updateOption(i, e.target.value)} />
              <button type="button" className="icon-button" onClick={() => removeOption(i)}
                disabled={(field.options || []).length <= 2} aria-label={`Remove option ${i + 1}`}>x</button>
            </div>
          ))}
          <button type="button" className="pipeline-skill-add" onClick={addOption}>+ Add option</button>
        </div>
      )}

      {validationFields.length > 0 && (
        <div className="form-field-validation">
          <span className="job-form-block-label">Validation (optional)</span>
          <div className="form-field-validation-grid">
            {validationFields.map((vf) => (
              <label key={vf.key} className={vf.wide ? "form-field-validation-wide" : ""}>
                <span>{vf.label}</span>
                <input
                  type={vf.type}
                  value={field.validation?.[vf.key] ?? ""}
                  onChange={(e) => onUpdateValidation(vf.key, e.target.value)}
                />
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FormFieldsEditorModal({ open, jobId, onClose }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    if (!open || !jobId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getFormFields(jobId);
        if (!cancelled) setFields(data.map(withLocalId));
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to load form fields");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, jobId]);

  if (!open) return null;

  const updateField = (localId, patch) => {
    setFields((prev) => prev.map((f) => (f._localId === localId ? { ...f, ...patch } : f)));
  };

  const updateValidation = (localId, key, rawValue) => {
    setFields((prev) => prev.map((f) => {
      if (f._localId !== localId) return f;
      const validation = { ...(f.validation || {}) };
      if (rawValue === "") delete validation[key];
      else validation[key] = key === "pattern" || key.endsWith("Date") ? rawValue : Number(rawValue);
      return { ...f, validation };
    }));
  };

  const addField = () => setFields((prev) => [...prev, blankField()]);
  const removeField = (localId) => setFields((prev) => prev.filter((f) => f._localId !== localId));
  const reorder = (from, to) => {
    if (from === to) return;
    setFields((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleSave = async () => {
    setError("");
    if (!fields.length) { setError("Add at least one field."); return; }
    for (const f of fields) {
      if (!f.label.trim()) { setError("Every field needs a label."); return; }
      if (FORM_CHOICE_TYPES.has(f.type)) {
        const opts = (f.options || []).map((o) => o.trim()).filter(Boolean);
        if (opts.length < 2) { setError(`"${f.label}" needs at least 2 options.`); return; }
      }
    }
    setSaving(true);
    try {
      const payload = fields.map(({ _localId, ...rest }) => rest);
      const saved = await saveFormFields(jobId, payload);
      toast.success("Form fields saved.");
      setFields(saved.map(withLocalId));
      onClose?.();
    } catch (err) {
      setError(err.message || "Unable to save form fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="recruiter-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="recruiter-modal form-fields-editor-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="recruiter-modal-header">
          <div>
            <h2>Form fields</h2>
            <p>Add, remove, reorder, and validate what candidates are asked to fill in.</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">x</button>
        </div>

        <div className="job-form">
          {error && <div className="job-form-error">{error}</div>}
          {loading ? (
            <div className="recruiter-empty">Loading fields…</div>
          ) : (
            <div className="form-field-builder-list">
              {fields.map((field, index) => (
                <FieldCard
                  key={field._localId}
                  field={field}
                  index={index}
                  total={fields.length}
                  onUpdate={(patch) => updateField(field._localId, patch)}
                  onUpdateValidation={(key, value) => updateValidation(field._localId, key, value)}
                  onRemove={() => removeField(field._localId)}
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); if (dragIndex != null) reorder(dragIndex, index); setDragIndex(null); }}
                />
              ))}
            </div>
          )}
          <button type="button" className="secondary-button" onClick={addField}>+ Add field</button>
        </div>

        <div className="recruiter-modal-actions">
          <button className="secondary-button" type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="primary-button" type="button" onClick={handleSave} disabled={saving || loading}>
            {saving ? "Saving…" : "Save fields"}
          </button>
        </div>
      </div>
    </div>
  );
}
